// ENG-5963: pure maintenance-window evaluation.
//
// The manager gates ALL restart-causing maintenance (agt-cli self-update,
// Claude Code binary upgrade, non-urgent reprovision/drift/reaper restarts) to
// a per-host maintenance window so updates land at a quiet, predictable hour
// instead of interrupting a live conversation at any time of day (the Kylie
// 2026-06-03 mid-conversation self-update restart).
//
// This module is deliberately PURE and dependency-free — same shape as
// self-update-coalesce.ts — so the window logic is trivially unit-testable,
// including the DST boundaries that make naive UTC-offset math wrong. All
// wall-clock reasoning goes through `Intl.DateTimeFormat`, which resolves the
// correct offset for the given instant (the same approach daily-session.ts
// already uses for agent-timezone date rollover).

/** Default window when a host has no override: 01:00–02:00 local. */
export const DEFAULT_WINDOW_START = '01:00';
export const DEFAULT_WINDOW_END = '02:00';

/** Env kill-switch for incident response — `1` disables window gating. */
export function isMaintenanceWindowDisabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env['AGT_MAINTENANCE_WINDOW_DISABLE'] === '1';
}

export interface MaintenanceWindowInput {
  /** 'HH:MM' (24h) or null/blank → default start. */
  start?: string | null;
  /** 'HH:MM' (24h) or null/blank → default end. */
  end?: string | null;
  /** IANA tz (e.g. 'Australia/Sydney') or null/blank/'UTC' → 'UTC'. */
  timezone?: string | null;
}

export interface ResolvedWindow {
  start: string;
  end: string;
  timezone: string;
}

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Validate + normalize an 'HH:MM' (24h) string. Returns null for anything that
 * isn't a well-formed time, so callers fall back to their default rather than
 * silently building a broken window. Mirrors the leniency of getTeamTimezone
 * (trim, treat unusable input as "unset").
 */
export function normalizeHhmm(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return HHMM_RE.test(trimmed) ? trimmed : null;
}

/** Resolve raw config (with nulls) into a concrete window, applying defaults. */
export function resolveWindow(input: MaintenanceWindowInput): ResolvedWindow {
  const tzRaw = typeof input.timezone === 'string' ? input.timezone.trim() : '';
  return {
    start: normalizeHhmm(input.start) ?? DEFAULT_WINDOW_START,
    end: normalizeHhmm(input.end) ?? DEFAULT_WINDOW_END,
    // Blank/whitespace → UTC. An explicit 'UTC' is also UTC. (Unlike
    // getTeamTimezone we keep 'UTC' here because this is the *evaluation* tz,
    // not an inherit-or-not decision.)
    timezone: tzRaw.length > 0 ? tzRaw : 'UTC',
  };
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
}

/**
 * Current local wall-clock time as 'HH:MM' in the given IANA zone for the
 * instant `now`. DST-correct: `Intl.DateTimeFormat` resolves the offset for
 * that instant. Falls back to UTC if the tz string is invalid (same defensive
 * try/catch as daily-session.ts:todayLocalIso).
 */
export function localHhmm(now: Date, timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);
    const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
    const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
    // Intl can render midnight as '24' under hour12:false in some engines;
    // normalize to '00' so minutes-of-day math stays in [0, 1440).
    const h = hh === '24' ? '00' : hh;
    return `${h}:${mm}`;
  } catch {
    const h = String(now.getUTCHours()).padStart(2, '0');
    const m = String(now.getUTCMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
}

/**
 * Is `now` within the maintenance window? Handles wrap-around windows that
 * cross midnight (start > end, e.g. 23:30–00:30). The window is half-open
 * `[start, end)` so a 01:00–02:00 window covers 01:00:00–01:59:59 and the
 * 02:00 tick is already out (avoids double-counting back-to-back windows).
 *
 * A zero-width window (start === end) is treated as permanently CLOSED, never
 * "always open" — a misconfiguration should fail safe toward *not* updating
 * (use AGT_MAINTENANCE_WINDOW_DISABLE to intentionally disable gating).
 */
export function isWithinMaintenanceWindow(input: MaintenanceWindowInput, now: Date): boolean {
  const w = resolveWindow(input);
  const start = hhmmToMinutes(w.start);
  const end = hhmmToMinutes(w.end);
  if (start === end) return false;
  const cur = hhmmToMinutes(localHhmm(now, w.timezone));
  if (start < end) return cur >= start && cur < end;
  // Wrap-around: window spans midnight.
  return cur >= start || cur < end;
}

/**
 * Wall-clock minutes until the window next opens (0 if currently in-window).
 * This is an approximation across DST transitions (a "day" can be 23 or 25h),
 * which is fine for a human-facing "deferring ~N min" log hint — the actual
 * gate always re-evaluates `isWithinMaintenanceWindow` against real time.
 */
export function minutesUntilWindowOpen(input: MaintenanceWindowInput, now: Date): number {
  if (isWithinMaintenanceWindow(input, now)) return 0;
  const w = resolveWindow(input);
  const start = hhmmToMinutes(w.start);
  const cur = hhmmToMinutes(localHhmm(now, w.timezone));
  return cur <= start ? start - cur : 1440 - cur + start;
}

/** One-line log hint for deferral, e.g. `01:00 Australia/Sydney (~312 min)`. */
export function formatWindowDeferLogLine(input: MaintenanceWindowInput, now: Date): string {
  const w = resolveWindow(input);
  const mins = minutesUntilWindowOpen(input, now);
  return `next maintenance window ${w.start} ${w.timezone} (~${mins} min)`;
}
